import { PlaybookCardsSection } from '@/playbooks/components/PlaybookCardsSection'
import {
  FormValues,
  PlaybookDeleteModal,
} from '@/playbooks/components/PlaybookDeleteModal'
import { PlaybookRenameModal } from '@/playbooks/components/PlaybookRenameModal'
import { PlaybookVisibilityModal } from '@/playbooks/components/PlaybookVisibilityModal'
import { Footer } from '@/shared/components/Footer'
import { SidebarLayout } from '@/shared/components/SidebarLayout'
import { Button } from '@/shared/components/ui/button'
import { Typography } from '@/shared/components/ui/typography'
import { useDeletePlaybook } from '@/shared/hooks/data-hooks/playbooks/useDeletePlaybook'
import { useDuplicatePlaybook } from '@/shared/hooks/data-hooks/playbooks/useDuplicatePlaybook'
import { useUpdatePlaybook } from '@/shared/hooks/data-hooks/playbooks/useUpdatePlaybook'
import { useWorkspace } from '@/shared/hooks/data-hooks/workspaces/useWorkspace'
import { useToast } from '@/shared/hooks/useToast'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { ApiPlaybookListItem } from '@/types/playbook'
import EditOutlined from '@mui/icons-material/EditOutlined'
import React, { useEffect, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'

const AIPlaybooks: React.FC = () => {
  const [playbookToDelete, setPlaybookToDelete] =
    useState<ApiPlaybookListItem | null>(null)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
  const [playbookToRename, setPlaybookToRename] =
    useState<ApiPlaybookListItem | null>(null)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [playbookToChangeVisibility, setPlaybookToChangeVisibility] =
    useState<ApiPlaybookListItem | null>(null)
  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false)

  const { mutateAsync: deletePlaybook } = useDeletePlaybook()
  const { mutateAsync: duplicatePlaybook } = useDuplicatePlaybook()
  const { mutateAsync: updatePlaybook } = useUpdatePlaybook()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { workspaceId } = useWorkspaceContext()
  const { data: { data: workspace } = {} } = useWorkspace(workspaceId)

  useEffect(() => {
    document.title = 'Saved Playbooks | Mydra'
  }, [])

  const handleDeleteConfirmation = async ({ confirmed }: FormValues) => {
    if (!playbookToDelete?._id || !confirmed) {
      setIsConfirmDeleteOpen(false)
      return
    }

    try {
      await deletePlaybook(playbookToDelete._id)
      setIsConfirmDeleteOpen(false)
      toast({
        title: 'Playbook deleted',
        description: 'The playbook has been removed successfully.',
      })
    } catch {
      toast({
        title: 'Error deleting playbook',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleRename = async (
    playbook: ApiPlaybookListItem,
    newTitle: string
  ) => {
    try {
      await updatePlaybook({
        id: playbook._id,
        playbookData: { title: newTitle },
      })
      toast({
        title: 'Playbook renamed',
        description: 'The playbook has been renamed successfully.',
      })
      setIsRenameModalOpen(false)
    } catch {
      toast({
        title: 'Error renaming playbook',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleDuplicate = async (playbook: ApiPlaybookListItem) => {
    try {
      const { data: duplicatedPlaybook } = await duplicatePlaybook({
        fromPlaybookId: playbook._id,
      })

      toast({
        title: 'Playbook duplicated',
        description: 'Redirecting to the duplicated playbook…',
      })

      navigate(`/playbooks/${duplicatedPlaybook._id}`)
    } catch {
      toast({
        title: 'Error duplicating playbook',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  useEffect(() => {
    window.Beacon?.('config', {
      hideFABOnMobile: true,
    })

    return () => {
      window.Beacon?.('config', {
        hideFABOnMobile: false,
      })
    }
  }, [])

  return (
    <SidebarLayout>
      <div className="mx-auto h-full bg-white md:px-5 md:pt-4">
        <div
          className="flex flex-col px-6 md:rounded-xl md:px-10"
          style={{
            background:
              'linear-gradient(180deg, rgba(187, 161, 230, 0.2) 0%, rgba(243, 167, 123, 0.20) 8.67%, rgba(255, 255, 255, 0.20) 25.22%), #FFF',
            backgroundSize: '100% 1027px',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top',
          }}
        >
          <div className="mb-6 mt-14 flex items-center justify-between">
            <header className="flex flex-col gap-2">
              <Typography variant="h2" className="font-semibold">
                Saved Playbooks
              </Typography>
              <Typography variant="subtitle" className="text-primary">
                Your workspace for organizing all the playbooks you create and
                save over time.
              </Typography>
            </header>
            <Button
              variant="outline"
              className="hidden sm:inline-flex"
              size="sm"
              asChild
            >
              <Link to="/dashboard?new=true">
                <EditOutlined sx={{ fontSize: 16 }} />
                Create New
              </Link>
            </Button>
          </div>

          {/* Content */}
          <div className="mt-14 flex flex-col gap-4">
            <Typography
              variant="subtitle"
              className="font-semibold text-primary-light"
            >
              Recent Creations
            </Typography>
            <PlaybookCardsSection
              hideCategoryTags
              cardVariant="withMenu"
              onPlaybookAction={(action, playbook) => {
                switch (action) {
                  case 'delete':
                    setPlaybookToDelete(playbook)
                    setIsConfirmDeleteOpen(true)
                    break
                  case 'rename':
                    setPlaybookToRename(playbook)
                    setIsRenameModalOpen(true)
                    break
                  case 'visibility':
                    setPlaybookToChangeVisibility(playbook)
                    setIsVisibilityModalOpen(true)
                    break
                  case 'duplicate':
                    handleDuplicate(playbook)
                    break
                  default:
                    break
                }
              }}
            />
          </div>
        </div>
        <div className="md:hidden">
          <Footer />
        </div>

        {/* Floating Create button for mobile */}
        <div className="pointer-events-none fixed bottom-6 right-6 z-20 sm:hidden">
          <div className="pointer-events-auto">
            <Button
              variant="outline"
              size="lg"
              className="rounded-full px-4 py-3"
              asChild
            >
              <Link to="/dashboard?new=true">
                <EditOutlined sx={{ fontSize: 16 }} />
                Create New
              </Link>
            </Button>
          </div>
        </div>
        <PlaybookDeleteModal
          playbook={playbookToDelete}
          onSubmit={handleDeleteConfirmation}
          isOpen={isConfirmDeleteOpen}
        />
        <PlaybookRenameModal
          playbook={playbookToRename}
          isOpen={isRenameModalOpen}
          onOpenChange={setIsRenameModalOpen}
          onSubmit={async (values) => {
            if (values && playbookToRename) {
              await handleRename(playbookToRename, values.title)
            }
          }}
        />
        <PlaybookVisibilityModal
          defaultValues={{
            visibility: playbookToChangeVisibility?.isPublic
              ? 'public'
              : 'private',
          }}
          open={isVisibilityModalOpen}
          onOpenChange={setIsVisibilityModalOpen}
          plan={workspace?.subscription?.subscriptionPlan?.type}
          onSubmit={async (values) => {
            if (!values || !playbookToChangeVisibility?._id) {
              setIsVisibilityModalOpen(false)
              return
            }

            try {
              await updatePlaybook({
                id: playbookToChangeVisibility._id,
                playbookData: { isPublic: values.visibility === 'public' },
              })

              toast({
                title: 'Visibility updated',
                description: `Playbook is now ${values.visibility}.`,
              })

              setIsVisibilityModalOpen(false)
            } catch {
              toast({
                title: 'Error updating visibility',
                description: 'Please try again.',
                variant: 'destructive',
              })
            }
          }}
        />
      </div>
    </SidebarLayout>
  )
}

export default AIPlaybooks
